import type {
  ImpactCategory,
  MatrixCellData,
  MatrixEntry,
  RiskScoringLevel,
  RiskScoringSettingsActions,
  RiskScoringSettingsState,
} from '@risksmart-app/atomic-ui';
import { useEffect, useRef } from 'react';
import { useGetLatestRiskAssessmentResultConfig } from 'src/hooks/queries';
import { create } from 'zustand';

import { analyzeChanges } from './change-detection';
import {
  areImpactCategoriesComplete,
  areImpactLevelsComplete,
  areLikelihoodLevelsComplete,
  isMatrixComplete,
} from './validation';

export interface ScoringSettingsData {
  likelihoodLevels: RiskScoringLevel[];
  impactLevels: RiskScoringLevel[];
  impactCategories: ImpactCategory[];
  impactAggregation: 'average' | 'maximum';
  matrix: MatrixEntry[];
  id: string;
  originalTimestamp: string;
}

type State = {
  // UI state
  isMultiImpactEnabled: boolean;
  selectedMethodology: 'impact-likelihood' | 'multi-impact';
  isMatrixInverted: boolean;
  stashedImpactCategories: ImpactCategory[];

  // Data
  current: ScoringSettingsData | null;
  initial: ScoringSettingsData | null;
};

type Actions = {
  hydrate: (data: ScoringSettingsData) => void;
  reset: () => void;
  setMultiImpactEnabled: (enabled: boolean) => void;
  setSelectedMethodology: (
    methodology: 'impact-likelihood' | 'multi-impact'
  ) => void;
  addLikelihoodLevel: () => void;
  addImpactLevel: () => void;
  addImpactCategory: () => void;
  deleteLikelihoodLevel: (value: number) => void;
  deleteImpactLevel: (value: number) => void;
  deleteImpactCategory: (name: string) => void;
  matrixCellClick: (cell: MatrixCellData) => void;
  setMatrixInverted: (inverted: boolean) => void;
  setImpactAggregation: (aggregation: 'average' | 'maximum') => void;
  resetToInitial: () => void;
};

const getNextValue = (levels: { value: number }[]) => {
  if (levels.length === 0) {
    return 1;
  }

  return Math.max(...levels.map((level) => level.value)) + 1;
};

const initialState: State = {
  isMultiImpactEnabled: false,
  selectedMethodology: 'impact-likelihood',
  isMatrixInverted: false,
  stashedImpactCategories: [],
  current: null,
  initial: null,
};

export const useInternalStore = create<State & Actions>()((set, get) => ({
  ...initialState,

  hydrate: (data) => {
    const hasCategories = data.impactCategories.length > 0;
    set({
      current: data,
      initial: data,
      isMultiImpactEnabled: hasCategories,
      selectedMethodology: 'impact-likelihood',
      stashedImpactCategories: [],
    });
  },

  reset: () => {
    set(initialState);
  },

  setMultiImpactEnabled: (enabled) => {
    set((state) => {
      if (!state.current) {
        return { isMultiImpactEnabled: enabled };
      }

      if (enabled) {
        return {
          isMultiImpactEnabled: true,
          stashedImpactCategories: [],
          current: {
            ...state.current,
            impactCategories: state.stashedImpactCategories,
          },
        };
      }

      return {
        isMultiImpactEnabled: false,
        stashedImpactCategories: state.current.impactCategories,
        current: {
          ...state.current,
          impactCategories: [],
        },
      };
    });
  },

  setSelectedMethodology: (methodology) => {
    set({ selectedMethodology: methodology });
  },

  addLikelihoodLevel: () => {
    set((state) => {
      if (!state.current) {
        return state;
      }

      const nextValue = getNextValue(state.current.likelihoodLevels);
      const fallbackColor =
        state.current.likelihoodLevels[
          state.current.likelihoodLevels.length - 1
        ]?.color ?? '#E5E7EB';

      return {
        current: {
          ...state.current,
          likelihoodLevels: [
            ...state.current.likelihoodLevels,
            {
              value: nextValue,
              title: `Level ${nextValue}`,
              description: '-',
              color: fallbackColor,
            },
          ],
        },
      };
    });
  },

  addImpactLevel: () => {
    set((state) => {
      if (!state.current) {
        return state;
      }

      const nextValue = getNextValue(state.current.impactLevels);
      const fallbackColor =
        state.current.impactLevels[state.current.impactLevels.length - 1]
          ?.color ?? '#E5E7EB';

      return {
        current: {
          ...state.current,
          impactLevels: [
            ...state.current.impactLevels,
            {
              value: nextValue,
              title: `Level ${nextValue}`,
              description: '-',
              color: fallbackColor,
            },
          ],
        },
      };
    });
  },

  addImpactCategory: () => {
    set((state) => {
      if (!state.current) {
        return state;
      }

      return {
        current: {
          ...state.current,
          impactCategories: [
            ...state.current.impactCategories,
            {
              name: `New Category ${state.current.impactCategories.length + 1}`,
              color: '#474771',
            },
          ],
        },
      };
    });
  },

  deleteLikelihoodLevel: (value) => {
    set((state) => {
      if (!state.current) {
        return state;
      }

      const matrix = state.current.matrix
        .map((entry) => ({
          ...entry,
          likelihoodImpact: entry.likelihoodImpact.filter(
            (pair) => pair.likelihood !== value
          ),
        }))
        .filter((entry) => entry.likelihoodImpact.length > 0);

      return {
        current: {
          ...state.current,
          likelihoodLevels: state.current.likelihoodLevels.filter(
            (level) => level.value !== value
          ),
          matrix,
        },
      };
    });
  },

  deleteImpactLevel: (value) => {
    set((state) => {
      if (!state.current) {
        return state;
      }

      const matrix = state.current.matrix
        .map((entry) => ({
          ...entry,
          likelihoodImpact: entry.likelihoodImpact.filter(
            (pair) => pair.impact !== value
          ),
        }))
        .filter((entry) => entry.likelihoodImpact.length > 0);

      return {
        current: {
          ...state.current,
          impactLevels: state.current.impactLevels.filter(
            (level) => level.value !== value
          ),
          matrix,
        },
      };
    });
  },

  deleteImpactCategory: (name) => {
    set((state) => {
      if (!state.current) {
        return state;
      }

      return {
        current: {
          ...state.current,
          impactCategories: state.current.impactCategories.filter(
            (category) => category.name !== name
          ),
        },
      };
    });
  },

  matrixCellClick: (cell) => {
    console.debug('Matrix cell clicked', cell);
  },

  setMatrixInverted: (inverted) => {
    set({ isMatrixInverted: inverted });
  },

  setImpactAggregation: (aggregation) => {
    set((state) => {
      if (!state.current) {
        return state;
      }

      return { current: { ...state.current, impactAggregation: aggregation } };
    });
  },

  resetToInitial: () => {
    const { initial } = get();
    const hasCategories = (initial?.impactCategories.length ?? 0) > 0;
    set({
      current: initial,
      stashedImpactCategories: [],
      isMultiImpactEnabled: hasCategories,
      selectedMethodology: 'impact-likelihood',
    });
  },
}));

// --- Public wrapper hook ---

export interface RiskScoringSettingsStoreValue {
  state: RiskScoringSettingsState;
  actions: RiskScoringSettingsActions;
  isLoading: boolean;
  id: string | null;
  originalTimestamp: string | null;
  resetToInitial: () => void;
  refetch: () => Promise<unknown>;
}

export const useRiskScoringSettingsStore =
  (): RiskScoringSettingsStoreValue => {
    const { config, loading, id, originalTimestamp, refetch } =
      useGetLatestRiskAssessmentResultConfig();
    const store = useInternalStore();
    const lastHydratedIdRef = useRef<string | null>(null);

    useEffect(() => {
      if (loading || !config) {
        return;
      }

      const dataKey = `${id}-${originalTimestamp}`;

      if (lastHydratedIdRef.current === dataKey) {
        return;
      }

      lastHydratedIdRef.current = dataKey;

      const mapLevel = (r: {
        value: number;
        title: string;
        description?: string | null;
        color: string;
      }) => ({
        value: r.value,
        title: r.title,
        description: r.description ?? '',
        color: r.color,
      });

      store.hydrate({
        likelihoodLevels:
          config?.likelihood?.ratings
            ?.map(mapLevel)
            .sort((a, b) => a.value - b.value) ?? [],
        impactLevels:
          config?.impact?.ratings
            ?.map(mapLevel)
            .sort((a, b) => a.value - b.value) ?? [],
        impactCategories:
          config?.impact?.categories?.map((c) => ({
            name: c.name,
            color: c.color,
          })) ?? [],
        matrix:
          config?.matrix?.map((e) => ({
            title: e.title,
            value: e.value,
            color: e.color,
            likelihoodImpact: e.likelihoodImpact,
          })) ?? [],
        impactAggregation: config?.impact?.aggregation ?? 'average',
        id: id!,
        originalTimestamp: originalTimestamp!,
      });
    }, [loading, config, store, id, originalTimestamp]);

    useEffect(() => {
      return () => {
        lastHydratedIdRef.current = null;
        useInternalStore.getState().reset();
      };
    }, []);

    const likelihoodComplete = areLikelihoodLevelsComplete(
      store.current?.likelihoodLevels ?? []
    );
    const impactComplete = areImpactLevelsComplete(
      store.current?.impactLevels ?? []
    );
    const categoriesComplete = areImpactCategoriesComplete(
      store.current?.impactCategories ?? []
    );
    const matrixComplete = isMatrixComplete(
      store.current?.matrix ?? [],
      store.current?.likelihoodLevels.length ?? 0,
      store.current?.impactLevels.length ?? 0
    );

    const state: RiskScoringSettingsState = {
      changeStatus:
        store.current && store.initial
          ? analyzeChanges(store.initial, store.current)
          : 'none',
      isMultiImpactEnabled: store.isMultiImpactEnabled,
      selectedMethodology: store.selectedMethodology,
      likelihoodLevels: store.current?.likelihoodLevels ?? [],
      impactLevels: store.current?.impactLevels ?? [],
      impactCategories: store.current?.impactCategories ?? [],
      matrix: store.current?.matrix ?? [],
      isMatrixInverted: store.isMatrixInverted,
      isLikelihoodLevelsComplete: likelihoodComplete,
      isImpactLevelsComplete: impactComplete,
      isImpactCategoriesComplete: categoriesComplete,
      isMatrixComplete: matrixComplete,
      isImpactLikelihoodComplete:
        likelihoodComplete && impactComplete && matrixComplete,
      isMultiImpactComplete: categoriesComplete,
      impactAggregation: store.current?.impactAggregation ?? 'average',
    };

    const actions: RiskScoringSettingsActions = {
      onMultiImpactEnabledChange: store.setMultiImpactEnabled,
      onSelectedMethodologyChange: store.setSelectedMethodology,
      onAddLikelihoodLevel: store.addLikelihoodLevel,
      onAddImpactLevel: store.addImpactLevel,
      onAddImpactCategory: store.addImpactCategory,
      onDeleteLikelihoodLevel: store.deleteLikelihoodLevel,
      onDeleteImpactLevel: store.deleteImpactLevel,
      onDeleteImpactCategory: store.deleteImpactCategory,
      onMatrixCellClick: store.matrixCellClick,
      onInvertMatrixChange: store.setMatrixInverted,
      onImpactAggregationChange: store.setImpactAggregation,
    };

    return {
      state,
      actions,
      isLoading: loading,
      id: store.current?.id ?? null,
      originalTimestamp: store.current?.originalTimestamp ?? null,
      resetToInitial: store.resetToInitial,
      refetch,
    };
  };
